import { Link } from 'react-router-dom';
import type { ContentItem } from '@/hooks/useContent';
import { formatViews } from '@/hooks/useContent';

const typeMeta: Record<ContentItem['type'], { label: string; icon: string; badge: string }> = {
  news: { label: '资讯', icon: 'ri-newspaper-line', badge: 'bg-primary-500 text-background-50' },
  video: { label: '视频', icon: 'ri-play-circle-line', badge: 'bg-accent-500 text-accent-950' },
  topic: { label: '话题', icon: 'ri-chat-3-line', badge: 'bg-secondary-700 text-background-50' },
  special: { label: '专题', icon: 'ri-bookmark-3-line', badge: 'bg-primary-500 text-background-50' },
};

interface ContentCardProps {
  item: ContentItem;
  variant?: 'grid' | 'wide';
}

export default function ContentCard({ item, variant = 'grid' }: ContentCardProps) {
  const meta = typeMeta[item.type];
  const viewsLabel = item.type === 'topic' && 'participants' in item
    ? `${formatViews(item.participants)} 人参与`
    : `${formatViews(item.views)} 阅读`;
  const duration = 'duration' in item ? item.duration : undefined;

  if (variant === 'wide') {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group flex gap-4 p-3 md:p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 cursor-pointer transition-colors"
      >
        <div className="relative shrink-0 w-32 h-24 md:w-52 md:h-32 rounded-md overflow-hidden bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 一起艹`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.badge}`}>
            {meta.label}
          </span>
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="font-medium text-sm md:text-base text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {item.title}
          </h3>
          <p className="mt-1.5 text-xs md:text-sm text-foreground-500 line-clamp-2 hidden sm:block">
            {item.summary}
          </p>
          <div className="mt-auto pt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {item.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {viewsLabel}
            </span>
            {duration ? (
              <span className="flex items-center gap-1 text-accent-700">
                <i className="ri-timer-line" />
                {duration}
              </span>
            ) : null}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 cursor-pointer transition-colors"
    >
      <div className="relative w-full aspect-video bg-background-200 overflow-hidden">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} - 一起艹`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className={`absolute top-2.5 left-2.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${meta.badge}`}>
          <i className={`${meta.icon} mr-1`} />
          {meta.label}
        </span>
        {duration ? (
          <span className="absolute bottom-2.5 right-2.5 px-2 py-0.5 rounded-md bg-secondary-950/70 text-background-50 text-[11px] font-medium">
            {duration}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <h3 className="font-medium text-sm md:text-base text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {item.title}
        </h3>
        <p className="mt-2 text-xs md:text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
        <div className="mt-auto pt-4 flex items-center justify-between gap-2 text-xs text-foreground-400">
          <span className="flex items-center gap-1 min-w-0">
            <i className="ri-user-3-line" />
            <span className="truncate">{item.author}</span>
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line" />
            {viewsLabel}
          </span>
        </div>
      </div>
    </Link>
  );
}